'use client';
import { useState, useEffect, useRef } from 'react';
import { Zap, Bot, Waypoints, Mic, Tag, ArrowRightLeft, Clock, Flame, Users, BellRing, BrainCircuit, Sparkles } from 'lucide-react';
import styles from './AutomationsModule.module.css';

const FLOW_SCRIPT = [
  { step: 0, node: 'trigger', type: 'in', text: 'Oi! Vi o anúncio da Mentoria.', delay: 1000 },
  { step: 1, node: 'bot', type: 'typing', text: '', delay: 1000 },
  { step: 2, node: 'bot', type: 'bot', text: '🤖 Olá! Tudo bem? Eu sou a IA do Aesir. Você quer saber sobre os valores ou ver o conteúdo do curso?', buttons: ['Ver Valores', 'Falar com Humano'], delay: 2000 },
  { step: 3, node: 'wait', type: 'in', text: 'Ver Valores', delay: 1500 },
  { step: 4, node: 'audio', type: 'typing', text: '', delay: 800 },
  { step: 5, node: 'audio', type: 'out', text: '▶ 🔘──────── 0:15', delay: 1500 }, 
  { step: 6, node: 'tag', type: 'system', text: 'Ação: Tag [Interessado] aplicada', delay: 1000 },
  { step: 7, node: 'transfer', type: 'bot', text: 'Ótimo! Estou te transferindo para o Thiago fechar a sua matrícula. Aguarde um instante! 🚀', delay: 2000 },
  { step: 8, node: 'transfer', type: 'system', text: 'Chat transferido para a fila: Comercial', delay: 3000 },
];

export function AutomationsModule() {
  const [currentStep, setCurrentStep] = useState(0);
  const [messages, setMessages] = useState<any[]>([]);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (currentStep >= FLOW_SCRIPT.length) {
      const resetTimer = setTimeout(() => {
        setMessages([]);
        setCurrentStep(0);
      }, 6000);
      return () => clearTimeout(resetTimer);
    }

    const scriptItem = FLOW_SCRIPT[currentStep];
    const timer = setTimeout(() => {
      if (scriptItem.type !== 'typing') {
        setMessages(prev => {
          const filtered = prev.filter(m => m.type !== 'typing');
          return [...filtered, scriptItem];
        });
      } else {
        setMessages(prev => [...prev, scriptItem]);
      }
      setCurrentStep(s => s + 1);
    }, scriptItem.delay);

    return () => clearTimeout(timer);
  }, [currentStep]);

  // Scroll do Chat
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages]);

  // Auto-Scroll do Canvas Horizontal sem puxar a tela
  useEffect(() => {
    if (canvasRef.current) {
      const activeElement = canvasRef.current.querySelector(`[data-active="true"]`) as HTMLElement;
      if (activeElement) {
        const container = canvasRef.current;
        // Calcula o centro para rolar o canvas na horizontal
        const scrollLeft = activeElement.offsetLeft - container.clientWidth / 2 + activeElement.clientWidth / 2;
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  }, [currentStep]);

  const isNodeActive = (nodeName: string) => {
    const activeNode = FLOW_SCRIPT[Math.min(currentStep, FLOW_SCRIPT.length - 1)]?.node;
    return activeNode === nodeName;
  };

  const isEdgeActive = (fromNode: string) => {
    if (fromNode === 'trigger') return currentStep >= 0 && currentStep <= 2;
    if (fromNode === 'bot') return currentStep >= 2 && currentStep <= 5;
    if (fromNode === 'audio') return currentStep >= 5 && currentStep <= 6;
    if (fromNode === 'tag') return currentStep >= 6 && currentStep <= 8;
    return false;
  };

  return (
    <section className={styles.darkSection}>
      <div className={styles.glowBackground}></div>
      
      <div className={styles.container}>
        
        <div className={styles.sectionHeader}>
          <span className={styles.badge}>Aesir Flow Builder</span>
          <h2 className={styles.title}>Automatize sem escrever <br/><span>uma linha de código.</span></h2>
          <p className={styles.subtitle}>
            Arraste, conecte e publique fluxos visuais que qualificam leads, enviam áudios, atribuem tags e escalam para humanos de forma 100% autônoma.
          </p>
        </div>

        <div className={styles.flowSplit}>
          
          {/* CANVAS HORIZONTAL (Com Auto-scroll e Pinos Laterais) */}
          <div className={styles.canvasContainer} ref={canvasRef}>
            
            <div data-active={isNodeActive('trigger')} className={`${styles.flowNode} ${styles.firstNode} ${isNodeActive('trigger') || isNodeActive('wait') ? styles.activeNode : ''}`}>
              <div className={styles.nodeHeader}>
                <div className={styles.nodeIcon} style={{ background: '#312e81', color: '#818cf8' }}><Zap size={14} /></div>
                <span className={styles.nodeTitle}>Trigger</span>
              </div>
              <div className={styles.nodeBody}>
                <div className={styles.nodeParam}><span className={styles.paramLabel}>Palavra</span><span className={styles.paramValue}>"Mentoria"</span></div>
              </div>
            </div>

            <div className={`${styles.flowEdge} ${isEdgeActive('trigger') ? styles.activeEdge : ''}`}></div>

            <div data-active={isNodeActive('bot')} className={`${styles.flowNode} ${isNodeActive('bot') ? styles.activeNode : ''}`}>
              <div className={styles.nodeHeader}>
                <div className={styles.nodeIcon} style={{ background: '#064e3b', color: '#34d399' }}><Bot size={14} /></div>
                <span className={styles.nodeTitle}>Agente IA</span>
              </div>
              <div className={styles.nodeBody}>
                <div className={styles.nodeParam}><span className={styles.paramLabel}>Ação</span><span className={styles.paramValue}>Menu Botões</span></div>
              </div>
            </div>

            <div className={`${styles.flowEdge} ${isEdgeActive('bot') ? styles.activeEdge : ''}`}></div>

            <div data-active={isNodeActive('audio')} className={`${styles.flowNode} ${isNodeActive('audio') ? styles.activeNode : ''}`}>
              <div className={styles.nodeHeader}>
                <div className={styles.nodeIcon} style={{ background: '#701a75', color: '#f472b6' }}><Mic size={14} /></div>
                <span className={styles.nodeTitle}>Enviar Mídia</span>
              </div>
              <div className={styles.nodeBody}>
                <div className={styles.nodeParam}><span className={styles.paramLabel}>Arquivo</span><span className={styles.paramValue}>valores.ogg</span></div>
              </div>
            </div>

            <div className={`${styles.flowEdge} ${isEdgeActive('audio') ? styles.activeEdge : ''}`}></div>

            <div data-active={isNodeActive('tag')} className={`${styles.flowNode} ${isNodeActive('tag') ? styles.activeNode : ''}`}>
              <div className={styles.nodeHeader}>
                <div className={styles.nodeIcon} style={{ background: '#7c2d12', color: '#fbbf24' }}><Tag size={14} /></div>
                <span className={styles.nodeTitle}>Ação CRM</span>
              </div>
              <div className={styles.nodeBody}>
                <div className={styles.nodeParam}><span className={styles.paramLabel}>Adicionar</span><span className={styles.paramValue}>[Interessado]</span></div>
              </div>
            </div>

            <div className={`${styles.flowEdge} ${isEdgeActive('tag') ? styles.activeEdge : ''}`}></div>

            <div data-active={isNodeActive('transfer')} className={`${styles.flowNode} ${styles.lastNode} ${isNodeActive('transfer') ? styles.activeNode : ''}`}>
              <div className={styles.nodeHeader}>
                <div className={styles.nodeIcon} style={{ background: '#1e3a8a', color: '#60a5fa' }}><ArrowRightLeft size={14} /></div>
                <span className={styles.nodeTitle}>Transbordo</span>
              </div>
              <div className={styles.nodeBody}>
                <div className={styles.nodeParam}><span className={styles.paramLabel}>Fila</span><span className={styles.paramValue}>Comercial</span></div>
              </div>
            </div>

          </div>

          {/* O CELULAR */}
          <div className={styles.phoneContainer}>
            <div className={styles.phoneMockup}>
              <div className={styles.phoneScreen}>
                
                <div className={styles.dynamicIsland}>
                  <div className={styles.cameraDot}></div>
                </div>

                <div className={styles.phoneHeader}>
                  <div className={styles.phoneAvatar}>A</div>
                  <div style={{display: 'flex', flexDirection: 'column'}}>
                    <div style={{fontWeight: 700, fontSize: '0.95rem'}}>Aesir Bot 🤖</div>
                    <div style={{fontSize: '0.7rem', color: '#8696a0'}}>online</div>
                  </div>
                </div>
                
                <div className={styles.phoneChat} ref={chatContainerRef}>
                  <div style={{alignSelf: 'center', background: '#182229', color: '#8696a0', fontSize: '0.7rem', padding: '4px 12px', borderRadius: '10px', margin: '4px 0'}}>
                    Hoje
                  </div>
                  {messages.map((msg, idx) => {
                    if (msg.type === 'system') return <div key={idx} className={styles.bubbleSystem}>{msg.text}</div>;
                    if (msg.type === 'typing') return (
                      <div key={idx} className={styles.typingIndicator}><span></span><span></span><span></span></div>
                    );

                    const bubbleClass = msg.type === 'in' ? styles.bubbleIn : msg.type === 'bot' ? styles.bubbleBot : styles.bubbleOut;
                    
                    return (
                      <div key={idx} style={{display: 'flex', flexDirection: 'column', alignSelf: msg.type === 'in' ? 'flex-end' : 'flex-start', maxWidth: '85%'}}>
                        <div className={`${styles.bubble} ${bubbleClass}`}>
                          {msg.text}
                          <span style={{float: 'right', marginLeft: '10px', marginTop: '6px', fontSize: '0.6rem', color: 'rgba(255,255,255,0.5)'}}>
                            10:42
                          </span>
                        </div>
                        {msg.buttons && (
                          <div style={{display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '4px'}}>
                            {msg.buttons.map((btn: string, bIdx: number) => (
                              <div key={bIdx} className={styles.bubbleBtn}>{btn}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className={styles.phoneInputBar}>
                  <div className={styles.phoneInputFake}>Mensagem</div>
                  <div className={styles.phoneMicBtn}><Mic size={18}/></div>
                </div>

              </div>
            </div>
          </div>

        </div>

        {/* RESTO DO COMPONENTE (IA GRID E FOLLOW UP) MANTIDO INTACTO */}
        <div className={styles.sectionHeader} style={{marginBottom: '3rem'}}>
          <h2 className={styles.title} style={{fontSize: '2.5rem'}}>Agentes Autônomos de <span>Inteligência Artificial</span></h2>
        </div>
        
        <div className={styles.aiGrid}>
          
          <div className={`${styles.aiCard} ${styles.featureGlow}`}>
            <div className={styles.aiIcon} style={{color: '#d946ef', background: 'rgba(217, 70, 239, 0.1)', position: 'relative'}}>
              <BrainCircuit size={32}/>
              <Sparkles size={16} color="#fbbf24" style={{position: 'absolute', top: 10, right: 10}}/>
            </div>
            <h4>Auto-Aprendizado Contínuo</h4>
            <p>O recurso mais poderoso do mercado. Toda madrugada, a IA do Aesir analisa as conversas dos seus melhores vendedores, aprende como eles quebram objeções e cria bases de conhecimento automaticamente para usar no dia seguinte. <strong>Fica mais inteligente a cada venda fechada.</strong></p>
          </div>

          <div className={styles.aiCard}>
            <div className={styles.aiIcon}><Bot size={32}/></div>
            <h4>Qualificação de Leads</h4>
            <p>O robô conversa de forma natural, faz as perguntas certas e só chama a equipe quando o cliente estiver qualificado.</p>
          </div>
          
          <div className={styles.aiCard}>
            <div className={styles.aiIcon} style={{color: '#34d399', background: 'rgba(52, 211, 153, 0.1)'}}><Tag size={32}/></div>
            <h4>Atribuição Automática</h4>
            <p>A IA entende o contexto da conversa e aplica tags no CRM sozinha (ex: "Urgente", "Dúvida Técnica").</p>
          </div>
          
          <div className={styles.aiCard}>
            <div className={styles.aiIcon} style={{color: '#fbbf24', background: 'rgba(251, 191, 36, 0.1)'}}><Waypoints size={32}/></div>
            <h4>Transbordo Inteligente</h4>
            <p>Se for dúvida de boleto, manda para o financeiro. Se for erro, pro suporte. Roteamento perfeito sem usar botões engessados.</p>
          </div>
        </div>

        <div className={styles.followUpSection}>
          <div className={styles.fuText}>
            <span className={styles.badge} style={{marginBottom: '1rem'}}>Recuperação de Vendas</span>
            <h3>O vendedor esqueceu?<br/>O sistema lembra.</h3>
            <p>Configure Follow-ups automáticos. Se um cliente ficar parado com a tag "Morno" por mais de 24h, o Aesir dispara um gatilho para puxar papo e reacender a venda.</p>
            <div style={{display: 'flex', gap: '1rem'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px', color: '#94a3b8', fontSize: '0.9rem'}}><Clock size={16} color="#f97316"/> Regras por tempo</div>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px', color: '#94a3b8', fontSize: '0.9rem'}}><Users size={16} color="#f97316"/> Filtro por Tag de CRM</div>
            </div>
          </div>
          
          <div className={styles.fuVisual}>
            <div className={styles.fuAlert}><BellRing size={16} style={{display: 'inline', marginRight: '6px'}}/> Disparo de Resgate Feito!</div>
            <div className={styles.fuCard}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <div style={{color: 'white', fontWeight: 800}}>Felipe Castro</div>
                <span className={styles.fuTag}><Flame size={12}/> Esfriando (24h sem resposta)</span>
              </div>
              <div style={{background: '#1e293b', padding: '12px', borderRadius: '8px', fontSize: '0.85rem', color: '#cbd5e1', fontStyle: 'italic', borderLeft: '3px solid #f97316'}}>
                "Opa Felipe, conseguiu dar uma olhada na proposta que te mandei ontem?"
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}